import { useEffect, useState } from "react";

// Not built on src/components/ui/progress.tsx (Radix Progress) — that
// component is determinate and expects a real value/aria-valuenow. Route
// chunk loading has no measurable progress to report, so exposing a fake
// percentage there would misrepresent state to assistive tech as well as
// sighted users. This is a separate, intentionally indeterminate bar.

interface RouteProgressBarProps {
  /** Delay in ms before the bar becomes visible. Defaults to 200. Exposed as a
   * prop so tests can override it instead of relying on real timers. */
  delayMs?: number;
}

export default function RouteProgressBar({ delayMs = 200 }: RouteProgressBarProps) {
  const [visible, setVisible] = useState(false);
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(showTimer);
  }, [delayMs]);

  useEffect(() => {
    if (!visible) return;
    // Width must commit at 0% in one paint before flipping to 90%, otherwise
    // the browser coalesces both states into a single paint and the CSS
    // transition never plays.
    const frame = requestAnimationFrame(() => setFilled(true));
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      {/* Decorative only — aria-hidden because an animated width percentage
          isn't meaningful information for assistive tech. The sr-only status
          span below carries the actual accessible announcement instead. */}
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 z-[100] h-[3px] w-full bg-transparent"
      >
        <div
          // Caps at 90%, never 100% — this component doesn't know when the
          // real chunk load finishes, it just unmounts when Suspense
          // resolves. Animating to 100% would imply completion right before
          // potentially needing to reset, which reads as broken. 90% over
          // 6s reads as "still working" for any load under ~6s (the
          // overwhelming majority) without ever visually stalling.
          className="h-full bg-[var(--deep-sky-denim)] transition-[width] duration-[6000ms] ease-out"
          style={{ width: filled ? "90%" : "0%" }}
        />
      </div>
      <span role="status" className="sr-only">
        Loading page…
      </span>
    </>
  );
}
